/**
 * Column layout grid per docs/specs/BUILDER_DESIGN_SPEC.md Section 5 — Column Grid.
 * Used when section.content.columns > 1 for columnable types (stat, team-member, card, icon-text, testimonial).
 * Responsive: < 768px max 2 columns, < 480px always 1 column.
 */

import type { ReactNode } from "react"

export interface ColumnGridProps {
  /** Number of columns (1–6) from content.columns */
  columns: number
  /** Number of items in the grid */
  itemCount: number
  children: ReactNode
}

const COLUMN_GRID_STYLES = `
  [data-column-grid] {
    display: grid;
    gap: 16px;
    width: 100%;
    margin: 0 auto;
  }
  /* NOS-234: multi-column grids fill their section container (the spec's 640px
     clamp compressed 3-column card/stat rows to ~200px columns while the
     heading spanned full width — the "smushed cards" bench bug). A single
     item keeps the narrow readable width. */
  [data-column-grid][data-cols="1"] { max-width: 640px; }
  [data-column-grid][data-cols="1"] { grid-template-columns: repeat(1, 1fr); }
  [data-column-grid][data-cols="2"] { grid-template-columns: repeat(2, 1fr); }
  [data-column-grid][data-cols="3"] { grid-template-columns: repeat(3, 1fr); }
  [data-column-grid][data-cols="4"] { grid-template-columns: repeat(4, 1fr); }
  [data-column-grid][data-cols="5"] { grid-template-columns: repeat(5, 1fr); }
  [data-column-grid][data-cols="6"] { grid-template-columns: repeat(6, 1fr); }
  @media (max-width: 767px) {
    [data-column-grid][data-cols="3"],
    [data-column-grid][data-cols="4"],
    [data-column-grid][data-cols="5"],
    [data-column-grid][data-cols="6"] { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 479px) {
    [data-column-grid][data-cols="2"],
    [data-column-grid][data-cols="3"],
    [data-column-grid][data-cols="4"],
    [data-column-grid][data-cols="5"],
    [data-column-grid][data-cols="6"] { grid-template-columns: repeat(1, 1fr); }
  }
`

export function ColumnGrid({ columns, itemCount, children }: ColumnGridProps) {
  const effectiveCols = Math.min(Math.max(1, columns), 6, itemCount)
  if (effectiveCols <= 0) return null

  return (
    <>
      <style>{COLUMN_GRID_STYLES}</style>
      <div data-column-grid data-cols={effectiveCols}>
        {children}
      </div>
    </>
  )
}
